// Approving a plan: every item switched on goes out. Posts are planned (with
// the reminder on the calendar and in the diary); emails and texts become
// scheduled campaigns through the same calls the email and SMS creators use
// (create, then send with sendNow false, which the campaign cron sends).
import moment from 'moment'
import momentTz from 'moment-timezone'
import { marketingApiService } from '../../../services/marketingApiService'
import { savePost } from '../../social/data/store'
import { forgetCampaigns } from '../../calendar/data/calendarData'
import { loadBusinessDetails, isBusinessDetailsComplete } from '../../../../marketing/businessDetails'
import { emailPayload } from './emailFor'
import { approvePlanMemory, saveProgress, progressFor } from './planStore'
import { broadcastToSites } from '../../../components/SendLocations'
import { textLinks } from './sms'

export const sendsMessages = (plan) => plan.items.some(it => it.on && (it.channel === 'email' || it.channel === 'sms'))

// Why a plan can't go yet ("" when it can)
// Times here are the salon's wall clock ("6pm" means 6pm at the salon; the
// server reads them in the salon's zone), so "has it passed?" is asked on the
// salon's clock too: an owner abroad was told a time had passed that the
// server would still take. `tz` is the salon's zone; `now` is for tests.
// No zone on the salon: London, as the server's sweep assumes (never the
// browser's own clock, which was the bug on a laptop abroad)
const salonNow = (tz) => {
  try { return moment(momentTz().tz(tz || 'Europe/London').format('YYYY-MM-DDTHH:mm:ss')) } catch (e) { return moment() }
}
// Emails or texts in it, and the sender's business details not filled in yet
export const needsBusinessDetails = (plan) => sendsMessages(plan) && !isBusinessDetailsComplete(loadBusinessDetails(localStorage.getItem('salonId')))

// askForBusinessDetails: the caller asks for missing details when approving
// (the Campaign Studio does), so they don't block the button
export const blockedBecause = (plan, { managed, now, tz, askForBusinessDetails } = {}) => {
  if (!plan.items.some(it => it.on)) return 'Switch on at least one thing to send.'
  if (!sendsMessages(plan)) return ''
  if (managed) return 'Your salon runs your marketing, so emails and texts are sent by them.'
  if (!askForBusinessDetails && needsBusinessDetails(plan)) return 'Add your business details in Marketing, Config before sending emails or texts. UK law needs them in every marketing email.'
  const at = now ? moment(now) : salonNow(tz)
  const gone = plan.items.filter(it => it.on && !moment(it.at).isAfter(at))
  if (gone.length) return 'Something in this plan is set for a time that’s passed. Pick a later time.'
  return ''
}

// Credits a campaign's emails and texts need beyond the free allowance, from
// the estimate-cost answers for one email and one text to this audience.
// Texts and emails share one wallet, so they're totalled together. Resolves
// to null when it can't be worked out (no estimate, or legacy billing, where
// nothing is refused) or { needed, have, short } in credits. The server makes
// the same check per campaign when it's scheduled; this says so up front.
export const creditsShort = ({ sms, email } = {}, { smsCount = 0, emailCount = 0 } = {}) => {
  const parts = [
    smsCount && sms ? { est: sms, n: smsCount } : null,
    emailCount && email ? { est: email, n: emailCount } : null,
  ].filter(Boolean)
  if (!parts.length || parts.some(p => p.est.billing !== 'credits')) return null
  // With one free pot for both (the server says freeShared, and gives it in
  // credits), texts and emails draw on the SAME allowance: take it off the
  // total once. Otherwise each channel has its own allowance.
  const shared = parts.every(p => p.est.freeShared)
  let needed = 0
  parts.forEach(({ est, n }) => {
    const perRecipient = Number(est.segmentsPerRecipient) || 1
    const units = n * (Number(est.recipientCount) || 0) * perRecipient
    const creditsPerUnit = (Number(est.creditsPerRecipient) || 0) / perRecipient
    const chargeable = shared ? units : Math.max(0, units - (Number(est.freeAvailable) || 0))
    needed += chargeable * creditsPerUnit
  })
  if (shared) needed = Math.max(0, needed - Math.max(...parts.map(p => Number(p.est.freeCredits) || 0)))
  const have = Math.max(...parts.map(p => Number(p.est.walletCredits) || 0))
  return { needed, have, short: Math.max(0, needed - have) }
}

// Each email and text carries its own source, e.g. bailey:fill:2026-10-01:sms.
// The backend returns the campaign it already made for that source (and
// channel) instead of making another, so however a retry happens (a tap, a
// reload, another device) the same email can't be scheduled twice. A campaign
// from the builder can hold several emails or texts, so its items are keyed
// by item id; one with no id of its own sends no source (nothing to dedupe on).
// A plan with two or three emails (a season: opening, halfway, last days)
// keys each by its item, or the server hands the first one back for the
// later ones and they're never scheduled (Ben's brief, 2026-10-06). The
// first email and text keep the plain channel key they always had, so a plan
// approved before this still finds its own.
export const sourceFor = (plan, item, campaignId = null) => {
  if (plan.key) return `bailey:${plan.key}:${!item.id || item.id === item.channel ? item.channel : item.id}`
  if (campaignId) return `campaign:${campaignId}:${item.id}`
  return undefined
}

// Sends what's switched on. `done` lists item ids already sent; items the
// saved progress says went are skipped too (Bailey's plans, which have a
// key), so trying again after a failure, or after a reload, sends only what's
// left. Progress is saved after every item; the plan counts as approved only
// when everything has gone.
//
// `alsoAt` (group owners): other locations each email and text also goes out
// from, as that location's own scheduled campaign (its clients, consent,
// credits; a slot guard re-checks THAT location's diary). An item counts as
// done only once it's scheduled here AND the other locations have answered,
// so a retry redoes both halves — safely: the campaign here comes back by its
// source, and the server hands back the copy it already made at each
// location rather than making (or sending) another. A location that can't
// take it (no credits, no business details) keeps it as a draft and says why
// in created.sites; that doesn't fail the item.
// Resolves to [{ id, ok, created?, error? }].
export const approvePlan = async (plan, { dispatch, ctx = {}, done = {}, campaignId = null, onStep = () => {}, alsoAt = [] } = {}) => {
  const results = []
  const audience = plan.audience || { segment: 'all' }
  const items = plan.items.filter(it => it.on)
  const saved = plan.key ? progressFor(plan.key) : null
  const made = { ...((saved && saved.done) || {}), ...done }
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i]
    if (made[item.id]) { results.push({ id: item.id, ok: true, created: made[item.id] }); continue } // eslint-disable-line no-continue
    onStep(item.id, 'working')
    try {
      let created
      if (item.channel === 'post' || item.channel === 'story') {
        const post = savePost({ ...item.post, status: 'planned', plannedAt: item.at, name: item.post.name || item.title, ...(campaignId ? { campaignId } : {}) })
        created = { channel: item.channel, id: post.id }
      } else {
        const when = moment(item.at)
        const scheduled = { scheduledDate: when.format('YYYY-MM-DD'), scheduledTime: when.format('HH:mm') }
        const name = `${plan.title}: ${item.title}`
        const body = item.channel === 'email'
          ? { name, ...emailPayload(item.email, ctx), ...(ctx.salonName ? { senderName: ctx.salonName } : {}) }
          // A text can't fill {{GiftCardLink}} or {{ShopLink}}: the salon's real links go in
          : { name, type: 'sms', message: textLinks(item.message, ctx.links || {}), ...(item.guard ? { guard: item.guard } : {}) }
        const source = sourceFor(plan, item, campaignId)
        // eslint-disable-next-line no-await-in-loop
        const campaign = await marketingApiService.createCampaign(dispatch, { ...body, audienceSegment: audience.segment || 'all', clientIds: [], ...scheduled, sendNow: false, ...(source ? { source } : {}), ...(campaignId ? { groupId: campaignId } : {}) })
        if (!campaign || !campaign._id) throw new Error('The campaign wasn’t saved.')
        // A campaign made on an earlier try may already be scheduled (or even
        // gone): only a draft needs scheduling.
        if (!campaign.status || campaign.status === 'draft') {
          // eslint-disable-next-line no-await-in-loop
          await marketingApiService.sendCampaign(dispatch, { campaignId: campaign._id, sendNow: false, ...(item.channel === 'sms' ? scheduled : {}) })
        }
        created = { channel: item.channel, id: campaign._id }
        if (alsoAt && alsoAt.length) {
          // eslint-disable-next-line no-await-in-loop
          created.sites = await broadcastToSites(campaign._id, alsoAt, { sendNow: false })
        }
      }
      made[item.id] = created
      if (plan.key) saveProgress(plan.key, made)
      results.push({ id: item.id, ok: true, created })
      onStep(item.id, 'done')
    } catch (e) {
      results.push({ id: item.id, ok: false, error: (e && e.message) || 'Something went wrong.' })
      onStep(item.id, 'failed')
    }
  }
  if (plan.key && results.length && results.every(r => r.ok)) approvePlanMemory(plan.key, results.map(r => r.created))
  forgetCampaigns()
  return results
}
